// ============================================
//  PHISHING ANALYSIS — NO AI (Social Engineering + QR)
// ============================================

import React, { useState, useRef } from "react";
import {
  FaArrowDown,
  FaArrowLeft,
  FaCheckCircle,
  FaCloudUploadAlt,
  FaCopy,
  FaEllipsisV,
  FaEnvelope,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaLayerGroup,
  FaShieldAlt,
  FaSpinner,
  FaTimes,
} from "react-icons/fa";
import "./PhishingAnalysis.css";
import { logAnalysis } from "../../utils/logAnalysis";
import { useAnalysisResult } from "../../context/AnalysisResultContext";
import { useAdvancedAnalysis, AdvancedAnalysisButton, AdvancedAnalysisCard, AdvancedAnalysisModal, foldSandboxVerdict } from "./AdvancedAnalysis";
import CustomRuleFindings from "./CustomRuleFindings";
import HomoglyphFindings from "./HomoglyphFindings";
import IocInfoPopover from "./IocInfoPopover";
import "./IocInfoPopover.css";
import { buildReceivedFlow, hopPillClass } from "../../utils/receivedFlow";

const formatKB = (bytes = 0) => Math.max(1, Math.round(bytes / 1024));

/* ---------------------- Verdict Styling ---------------------- */

const mapVerdictToClass = (verdict = "") => {
  const v = String(verdict || "").toLowerCase();
  if (v === "whitelisted" || v.includes("whitelist")) return "pill-ok";
  if (v.includes("needs review")) return "pill-review";
  if (v.includes("potentially malicious") || v === "potentially malicious") return "pill-warn";
  if (v === "malicious" || v === "high" || v.includes("malicious")) return "pill-danger";
  if (v.includes("phish") || v.includes("phishing")) return "pill-danger";
  if (v.includes("suspicious") || v === "suspicious" || v === "medium" || v === "reported") return "pill-warn";
  if (v.includes("spam")) return "pill-warn";
  if (v === "clean" || v === "low" || v === "safe" || v.includes("clean") || v.includes("safe")) return "pill-ok";
  return "pill-neutral";
};

const formatVerdict = (verdict = "") => {
  const raw = String(verdict || "");
  if (!raw) return "";
  if (raw.toLowerCase() === "suspicious") {
    return raw[0] === raw[0]?.toUpperCase() ? "Suspicious" : "suspicious";
  }
  return raw;
};

const mapVerdictToLabel = (verdict = "") => {
  if (!verdict) return "unknown";
  return formatVerdict(verdict);
};

// Shown in place of a provider verdict when that OSINT API hit its rate limit,
// so an analyst knows the data is missing (throttled) rather than "clean".
const RateTag = () => (
  <span
    className="intel-ratelimited"
    title="This provider's API rate limit was hit — reputation unavailable. Re-run shortly."
  >
    <FaExclamationTriangle aria-hidden="true" /> Rate limited
  </span>
);

/* ---------------------- Social Engineering Helpers ---------------------- */

const humanizeCat = (k = "") => {
  const map = {
    urgency: "Urgency / Pressure",
    payment: "Payments / Money",
    credential: "Credentials / Login",
    fear: "Fear / Threat",
  };
  return map[k] || k;
};

const buildSocialEngineeringSummary = (socialIntent) => {
  if (!socialIntent) return "Social engineering checks were not executed.";

  const level = String(socialIntent.level || "low").toLowerCase();
  const indicators = socialIntent.indicators || {};

  const categoriesWithHits = Object.entries(indicators)
    .map(([k, hits]) => ({ k, hits: Array.isArray(hits) ? hits : [] }))
    .filter((x) => x.hits.length > 0);

  if (categoriesWithHits.length === 0) {
    return "No obvious social engineering language was detected in the subject or body.";
  }

  const catNames = categoriesWithHits.map((x) => humanizeCat(x.k));
  const topCats = catNames.slice(0, 3).join(", ") + (catNames.length > 3 ? " and others" : "");

  if (level === "high") {
    return `The email contains strong social engineering indicators, primarily around ${topCats}.`;
  }
  if (level === "medium") {
    return `The email contains potential social engineering cues, mainly around ${topCats}.`;
  }
  return `Low-intensity persuasion cues were detected, mainly around ${topCats}.`;
};

const socialLevelToPill = (level = "") => {
  const l = String(level || "").toLowerCase();
  if (l === "high") return "pill-danger";
  if (l === "medium") return "pill-warn";
  return "pill-ok";
};

/* ---------------------- QR Helpers ---------------------- */

const parseQrPayload = (raw = "") => {
  const text = String(raw || "").trim();
  if (!text) return { type: "unknown", value: "" };

  if (/^https?:\/\/\S+/i.test(text)) return { type: "url", value: text };

  if (/^mailto:/i.test(text)) {
    const email = text.replace(/^mailto:/i, "").split("?")[0].trim();
    return { type: "email", value: email || text };
  }

  if (/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(text)) {
    return { type: "email", value: text.toLowerCase() };
  }

  if (/^tel:/i.test(text)) return { type: "phone", value: text.replace(/^tel:/i, "").trim() };

  if (/^upi:\/\//i.test(text)) return { type: "upi", value: text };
  if (/@upi\b/i.test(text) && !text.includes(" ")) return { type: "upi-id", value: text };

  return { type: "text", value: text };
};

const renderQrValue = (parsed) => {
  if (!parsed?.value) return <span className="muted">N/A</span>;

  if (parsed.type === "url") {
    return (
      <a href={parsed.value} target="_blank" rel="noreferrer" className="intel-link">
        {parsed.value}
      </a>
    );
  }

  if (parsed.type === "email") {
    return (
      <a href={`mailto:${parsed.value}`} className="intel-link">
        {parsed.value}
      </a>
    );
  }

  if (parsed.type === "phone") {
    return (
      <a href={`tel:${parsed.value}`} className="intel-link">
        {parsed.value}
      </a>
    );
  }

  return <span className="mono mono-wrap">{parsed.value}</span>;
};

const BULK_MAX_FILES = 25;

const toOneWordVerdict = (raw = "") => {
  const v = String(raw || "").toLowerCase();
  if (!v) return "Unknown";
  if (v.includes("whitelist")) return "Whitelisted";
  if (v.includes("malicious") && !v.includes("potentially")) return "Malicious";
  if (v.includes("phish")) return "Malicious";
  if (v.includes("suspicious") || v.includes("potentially")) return "Suspicious";
  if (v.includes("spam")) return "Spam";
  if (v.includes("clean") || v.includes("safe")) return "Clean";
  return String(raw).split(/\s+/)[0] || "Unknown";
};

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

export default function PhishingAnalysis() {
  const { standardResult, setStandardResult, clearStandardResult, standardFile, setStandardFile } = useAnalysisResult();

  const [mode, setMode] = useState("single"); // "single" | "bulk"

  // Selected single email lives in context so it survives sidebar navigation
  // (cleared on removal or full page refresh), matching the analysis result.
  const file = standardFile;
  const setFile = setStandardFile;
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [showRawHeader, setShowRawHeader] = useState(false);
  const [copyStatus, setCopyStatus] = useState("");
  const [activeTab, setActiveTab] = useState("header");

  const useAi = false;

  const [optionsOpen, setOptionsOpen] = useState(false);
  const abortRef = useRef(null);
  const [confirmSensitive, setConfirmSensitive] = useState(null);
  const [sensitiveChecking, setSensitiveChecking] = useState(false);

  // Bulk state
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkItems, setBulkItems] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkCurrent, setBulkCurrent] = useState(0);
  const [bulkError, setBulkError] = useState("");
  const [selectedBulkIndex, setSelectedBulkIndex] = useState(null);
  const [bulkDragOver, setBulkDragOver] = useState(false);
  const bulkAbortRef = useRef(null);
  const bulkCancelRef = useRef(false);

  const result =
    mode === "bulk"
      ? selectedBulkIndex != null
        ? bulkItems[selectedBulkIndex]?.result || null
        : null
      : standardResult;
  const setResult = setStandardResult;

  // Advanced (on-demand sandbox) analysis — shared across analysis pages.
  // Works for single uploads and for the currently-selected bulk item.
  const advFile =
    mode === "bulk"
      ? (selectedBulkIndex != null ? bulkFiles[selectedBulkIndex] || null : null)
      : file;
  const adv = useAdvancedAnalysis({
    file: advFile,
    result,
    endpoint: "/api/phishing-analysis-nai/advanced",
  });

  /* ------------------ File Selection ------------------ */

  const onChoose = (e) => {
    setResult(null);
    setError("");
    setFile(e.target.files?.[0] || null);
  };

  // Clear the selected single email (and any prior verdict) without picking a new one.
  const onRemoveFile = () => {
    clearStandardResult();
    setFile(null);
    setError("");
    setProgress(0);
  };

  /* ------------------ Upload With Progress ------------------ */

  const uploadWithProgress = (url, formData, onProgress, headers = {}) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      abortRef.current = xhr;
      xhr.open("POST", url, true);

      Object.entries(headers).forEach(([k, v]) => {
        if (v) xhr.setRequestHeader(k, v);
      });

      // Progress strategy:
      //   0–25%  → real upload bytes
      //  25–95%  → simulated curve while the server analyzes (eases out)
      //   100%   → response received
      const SIM_CAP = 95;
      let simTimer = null;
      let simValue = 25;

      const startSimulated = () => {
        if (simTimer) return;
        simValue = 25;
        onProgress?.(simValue);
        simTimer = setInterval(() => {
          const remaining = SIM_CAP - simValue;
          const step = Math.max(0.4, remaining * 0.04);
          simValue = Math.min(SIM_CAP, simValue + step);
          onProgress?.(Math.round(simValue));
        }, 220);
      };

      const stopSimulated = () => {
        if (simTimer) {
          clearInterval(simTimer);
          simTimer = null;
        }
      };

      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable && onProgress) {
          onProgress(Math.round((evt.loaded / evt.total) * 25));
        }
      };

      xhr.upload.onload = () => {
        startSimulated();
      };

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          stopSimulated();
          if (xhr.status >= 200 && xhr.status < 300) {
            onProgress?.(100);
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              reject(new Error("Invalid JSON from server"));
            }
          } else {
            let msg = `Upload failed: ${xhr.status} ${xhr.statusText}`;
            try {
              const parsed = JSON.parse(xhr.responseText || "{}");
              if (parsed?.error) msg = parsed.error;
            } catch {}
            reject(new Error(msg));
          }
        }
      };

      xhr.onerror = () => {
        stopSimulated();
        reject(new Error("Network error during upload"));
      };
      xhr.send(formData);
    });

  /* ------------------ Analyze ------------------ */

  const doAnalyze = async () => {
    const startTime = Date.now();

    setLoading(true);
    setProgress(0);
    setError("");
    setResult(null);
    setCopyStatus("");

    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("useAi", useAi ? "true" : "false");

      const token = sessionStorage.getItem("auth_token");
      const json = await uploadWithProgress("/api/phishing-analysis-nai", fd, (p) => setProgress(p), token ? { Authorization: `Bearer ${token}` } : {});

      const processingTimeMs = Date.now() - startTime;
      setResult(json);
      setActiveTab("header");
      setShowRawHeader(false);
      setOptionsOpen(false);

      const verdict = json?.verdict || "Unknown";
      logAnalysis({
        analysis_type: "standard",
        filename: file?.name ?? null,
        file_size_bytes: file?.size ?? null,
        subject: json?.headers?.subject ?? null,
        sender: json?.headers?.from ?? null,
        recipient: json?.headers?.to ?? null,
        processing_time_ms: processingTimeMs,
        verdict,
        verdict_class: mapVerdictToClass(verdict),
      });
    } catch (err) {
      setError(err.message || "Analysis failed");
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const onAnalyze = async () => {
    if (!file) return;
    setSensitiveChecking(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = sessionStorage.getItem("auth_token");
      const res = await fetch("/api/phishing-analysis/precheck", {
        method: "POST",
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const check = await res.json();
        if (check.sensitive) {
          setSensitiveChecking(false);
          setConfirmSensitive({
            classification: check.classification,
            reasons: check.reasons,
            onConfirm: () => { setConfirmSensitive(null); doAnalyze(); },
            onCancel: () => setConfirmSensitive(null),
          });
          return;
        }
      }
    } catch { /* precheck failure — proceed */ }
    setSensitiveChecking(false);
    doAnalyze();
  };

  const onCancelUpload = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      setLoading(false);
      setProgress(0);
      setError("Upload cancelled");
    }
  };

  /* ------------------ Bulk ------------------ */

  const mergeBulkFiles = (existing, rawFiles) => {
    const accepted = [...existing];
    let skippedExt = 0;
    let skippedLimit = 0;
    for (const f of rawFiles) {
      const name = (f.name || "").toLowerCase();
      if (!name.endsWith(".eml") && !name.endsWith(".msg")) {
        skippedExt++;
        continue;
      }
      if (accepted.length >= BULK_MAX_FILES) {
        skippedLimit++;
        continue;
      }
      accepted.push(f);
    }
    const msgs = [];
    if (skippedLimit > 0) {
      msgs.push(
        `Limit is ${BULK_MAX_FILES} files per batch — skipped ${skippedLimit} extra.`
      );
    }
    if (skippedExt > 0) {
      msgs.push(`Skipped ${skippedExt} non-.eml/.msg file${skippedExt === 1 ? "" : "s"}.`);
    }
    setBulkError(msgs.join(" "));
    return accepted;
  };

  const onBulkChoose = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file later
    if (picked.length === 0) return;
    setBulkFiles((prev) => mergeBulkFiles(prev, picked));
    setBulkItems([]);
    setSelectedBulkIndex(null);
  };

  const onBulkDragOver = (e) => {
    e.preventDefault();
    setBulkDragOver(true);
  };
  const onBulkDragLeave = () => setBulkDragOver(false);
  const onBulkDrop = (e) => {
    e.preventDefault();
    setBulkDragOver(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    if (dropped.length === 0) return;
    setBulkFiles((prev) => mergeBulkFiles(prev, dropped));
    setBulkItems([]);
    setSelectedBulkIndex(null);
  };

  const onRemoveBulkFile = (index) => {
    setBulkFiles((prev) => prev.filter((_, i) => i !== index));
    setBulkItems([]);
    setSelectedBulkIndex(null);
    setBulkError("");
  };

  const uploadOne = (singleFile) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      bulkAbortRef.current = xhr;
      xhr.open("POST", "/api/phishing-analysis-nai", true);
      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) return;
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new Error("Invalid JSON from server"));
          }
        } else {
          let msg = `Upload failed: ${xhr.status} ${xhr.statusText}`;
          try {
            const parsed = JSON.parse(xhr.responseText || "{}");
            if (parsed?.error) msg = parsed.error;
          } catch {}
          reject(new Error(msg));
        }
      };
      xhr.onerror = () => reject(new Error("Network error during upload"));
      const fd = new FormData();
      fd.append("file", singleFile);
      fd.append("useAi", "false");
      xhr.send(fd);
    });

  const precheckOne = async (f) => {
    try {
      const fd = new FormData();
      fd.append("file", f);
      const token = sessionStorage.getItem("auth_token");
      const res = await fetch("/api/phishing-analysis/precheck", {
        method: "POST",
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const check = await res.json();
        if (check.sensitive) return { fileName: f.name, classification: check.classification, reasons: check.reasons };
      }
    } catch {}
    return null;
  };

  const onBulkAnalyze = async () => {
    if (bulkFiles.length === 0 || bulkLoading) return;

    setSensitiveChecking(true);
    let proceedWithBulk = true;
    try {
      const precheckResults = await Promise.all(bulkFiles.map(precheckOne));
      const sensitiveFiles = precheckResults.filter(Boolean);
      if (sensitiveFiles.length > 0) {
        proceedWithBulk = await new Promise((resolve) => {
          setConfirmSensitive({
            isBulk: true,
            sensitiveFiles,
            onConfirm: () => { setConfirmSensitive(null); resolve(true); },
            onCancel: () => { setConfirmSensitive(null); resolve(false); },
          });
        });
      }
    } catch { /* precheck failure — proceed */ }
    setSensitiveChecking(false);
    if (!proceedWithBulk) return;

    bulkCancelRef.current = false;
    setBulkLoading(true);
    setBulkError("");
    setSelectedBulkIndex(null);
    setBulkCurrent(0);

    const initial = bulkFiles.map((f) => ({
      fileName: f.name,
      size: f.size,
      status: "pending",
      result: null,
      error: null,
    }));
    setBulkItems(initial);

    for (let i = 0; i < bulkFiles.length; i++) {
      if (bulkCancelRef.current) break;
      setBulkCurrent(i + 1);
      setBulkItems((prev) => {
        const next = [...prev];
        if (next[i]) next[i] = { ...next[i], status: "analyzing" };
        return next;
      });

      const startedAt = Date.now();
      try {
        const json = await uploadOne(bulkFiles[i]);
        const processingTimeMs = Date.now() - startedAt;
        setBulkItems((prev) => {
          const next = [...prev];
          if (next[i]) next[i] = { ...next[i], status: "done", result: json };
          return next;
        });
        const verdict = json?.verdict || "Unknown";
        logAnalysis({
          analysis_type: "standard",
          filename: bulkFiles[i]?.name ?? null,
          file_size_bytes: bulkFiles[i]?.size ?? null,
          subject: json?.headers?.subject ?? null,
          sender: json?.headers?.from ?? null,
          recipient: json?.headers?.to ?? null,
          processing_time_ms: processingTimeMs,
          verdict,
          verdict_class: mapVerdictToClass(verdict),
        });
      } catch (err) {
        setBulkItems((prev) => {
          const next = [...prev];
          if (next[i])
            next[i] = {
              ...next[i],
              status: "error",
              error: err?.message || "Analysis failed",
            };
          return next;
        });
      }
    }

    bulkAbortRef.current = null;
    setBulkLoading(false);
  };

  const onCancelBulk = () => {
    bulkCancelRef.current = true;
    if (bulkAbortRef.current) {
      try {
        bulkAbortRef.current.abort();
      } catch {}
    }
    setBulkLoading(false);
  };

  const onClearBulk = () => {
    setBulkFiles([]);
    setBulkItems([]);
    setBulkError("");
    setSelectedBulkIndex(null);
    setBulkCurrent(0);
  };

  const onSelectMode = (next) => {
    if (next === mode) return;
    setMode(next);
    setError("");
    setBulkError("");
    setSelectedBulkIndex(null);
    setActiveTab("header");
    setShowRawHeader(false);
    setOptionsOpen(false);
  };

  /* ------------------ Copy Raw Headers ------------------ */

  const handleCopyHeaders = async () => {
    const text = result?.headers?.raw;
    if (!text) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    }

    setTimeout(() => setCopyStatus(""), 2000);
  };

  /* ------------------ Derived Values ------------------ */

  const headerSec = result?.headerSecurity || {};
  const replyCheck = headerSec?.replyToCheck || null;

  const replyRiskClass =
    replyCheck?.risk === "high"
      ? "pill-danger"
      : replyCheck?.risk === "medium"
      ? "pill-warn"
      : "pill-ok";

  const headerRiskClass =
    headerSec?.riskLevel === "high"
      ? "pill-danger"
      : headerSec?.riskLevel === "medium"
      ? "pill-warn"
      : "pill-ok";

  const fromDomainForFlow = headerSec?.fromDomain || null;
  const receivedFlow = buildReceivedFlow(
    result?.headers?.received || [],
    fromDomainForFlow,
    result?.trustedDomains || [],
    result?.blacklistedDomains || []
  );

  // Social engineering
  const socialIntent = result?.socialIntent || null;
  const socialLevel = socialIntent?.level || "low";
  const socialScore = typeof socialIntent?.score === "number" ? socialIntent.score : 0;
  const socialIndicators = socialIntent?.indicators || {};
  const socialSummaryText = buildSocialEngineeringSummary(socialIntent);
  const socialPillClass = socialLevelToPill(socialLevel);

  // QR
  const qrFromAttachmentsForSummary = (result?.attachments || [])
    .filter((a) => a?.qr?.data)
    .map((a) => ({
      source: "attachment",
      name: a?.name || "-",
      mime: a?.mime || "-",
      qrText: a?.qr?.data || "",
    }));

  const qrFromBodyForSummary = (result?.qrFindings || []).map((q) => ({
    source: q?.type === "cid" ? "inline (cid)" : "inline (data-uri)",
    name: q?.fileName || q?.cid || "-",
    mime: q?.mime || "-",
    qrText: q?.qrText || "",
  }));

  const allQrForSummary = [...qrFromAttachmentsForSummary, ...qrFromBodyForSummary];
  const qrCount = allQrForSummary.length;

  const qrPreview = (() => {
    if (qrCount === 0) return null;
    const first = String(allQrForSummary[0]?.qrText || "").trim();
    if (!first) return "decoded value present";
    return first.length > 90 ? `${first.slice(0, 90)}…` : first;
  })();

  const vtDomainStats = headerSec?.vtDomain || null;

  let vtDomainScore = null;
  let vtDomainScoreClass = "summary-entity-vt summary-entity-vt-neutral";

  if (vtDomainStats && typeof vtDomainStats.positives === "number" && typeof vtDomainStats.total === "number") {
    const positives = vtDomainStats.positives;
    const total = vtDomainStats.total || 0;
    vtDomainScore = total ? `${positives}/${total}` : `${positives}/?`;
    vtDomainScoreClass =
      positives > 0 ? "summary-entity-vt summary-entity-vt-bad" : "summary-entity-vt summary-entity-vt-good";
  }

  const senderValidation = result?.senderValidation || {};
  const spfResult = senderValidation?.spf?.result || "unknown";
  const dkimResult = senderValidation?.dkim?.result || "unknown";
  const dmarcResult = senderValidation?.dmarc?.result || "unknown";
  const spfDetail = senderValidation?.spf?.detail;
  const dkimDetail = senderValidation?.dkim?.detail;
  const dmarcDetail = senderValidation?.dmarc?.detail;

  const getDomainFromAddr = (addr = "") => {
    const m = String(addr).match(/@([^>\s]+)/);
    return m ? m[1].toLowerCase() : "";
  };

  const fromAddress = result?.headers?.from || "Unknown sender";
  const toAddress = result?.headers?.to || "Unknown recipient";

  const fromDomainText = headerSec?.fromDomain || getDomainFromAddr(fromAddress) || "unknown";
  const toDomainText = getDomainFromAddr(toAddress);

  const senderType = fromDomainText && toDomainText && fromDomainText === toDomainText ? "internal" : "external";

  const urlsArr = result?.urls || [];

  let sourceIp = null;
  if (receivedFlow.length > 0 && receivedFlow[0].line) {
    const ipMatch = receivedFlow[0].line.match(/\b\d{1,3}(?:\.\d{1,3}){3}\b/);
    sourceIp = ipMatch ? ipMatch[0] : null;
  }

  const vtDomainUrl =
    fromDomainText && fromDomainText !== "unknown"
      ? `https://www.virustotal.com/gui/domain/${encodeURIComponent(fromDomainText)}/details`
      : null;

  const vtIpUrl = sourceIp
    ? `https://www.virustotal.com/gui/ip-address/${encodeURIComponent(sourceIp)}/details`
    : null;

  const attachmentCount = (result?.attachments || []).length;
  const primaryAttachment = attachmentCount === 1 ? result.attachments[0] : null;

  const formatControlSummary = (ctrl, label) => {
    if (!ctrl || !ctrl.result) return `${label}: not evaluated`;
    const res = String(ctrl.result).toLowerCase();
    const pass = res === "pass";
    const aligned = ctrl.alignedWithFrom;
    const compliant = pass && aligned ? "Compliant" : "Non-compliant";
    return `${label}: ${res.toUpperCase()} (${compliant})`;
  };

  const spfSummary = formatControlSummary(senderValidation.spf, "SPF");
  const dkimSummary = formatControlSummary(senderValidation.dkim, "DKIM");
  const dmarcSummary = formatControlSummary(senderValidation.dmarc, "DMARC");

  const primaryUrl = urlsArr[0] || null;

  const vtLabel = primaryUrl?.vt ? primaryUrl.vt.verdict || "unknown" : "not checked";
  const abuseLabel = primaryUrl?.abuseIpdb
    ? typeof primaryUrl.abuseIpdb.score === "number"
      ? `${primaryUrl.abuseIpdb.score}/100`
      : primaryUrl.abuseIpdb.classification || "unknown"
    : "not checked";
  const ipqsLabel = primaryUrl?.ipqs
    ? typeof primaryUrl.ipqs.score === "number"
      ? `${primaryUrl.ipqs.score}/100`
      : primaryUrl.ipqs.risk || "unknown"
    : "not checked";
  const urlscanLabel = primaryUrl?.urlscan ? primaryUrl.urlscan.verdict || "unknown" : "not checked";
  const otxLabel = primaryUrl?.otx ? primaryUrl.otx.verdict || "unknown" : "not checked";

  // Sandbox detonation, folded into the verdict as a third engine
  // (escalate-only): malicious → Malicious, suspicious → Needs Review.
  const detoMalicious = adv.detoMalicious;
  const detoSuspicious = adv.detoSuspicious;
  const detoComplete = adv.detoComplete;
  const detoVerdict = adv.detoVerdict;
  const baseVerdict = result?.verdict || "Unknown";
  const {
    verdict: finalVerdict,
    raisedBySandbox,
  } = foldSandboxVerdict(baseVerdict, { detoMalicious, detoSuspicious });
  const finalVerdictClass = mapVerdictToClass(finalVerdict);

  // Confirmed-malicious detonation is hard proof and clears the review flag.
  const analystReviewAdvised = detoMalicious ? false : !!result?.analystReviewAdvised;
  const analystReviewReason = result?.analystReviewReason || "";

  // Allowlisted IoCs are EXCLUDED from the verdict, but never relabel it: the
  // banner always shows the real computed verdict. When an allowlisted IoC was
  // independently flagged malicious/suspicious we surface it as a clear note,
  // and — when it was the *only* malicious factor (verdict reads benign) — keep
  // a RED CAUTION BORDER so the hidden threat is visible at a glance.
  const whitelistedMaliciousIndicators =
    (Array.isArray(result?.whitelistedMaliciousIndicators) && result.whitelistedMaliciousIndicators) ||
    (Array.isArray(result?.whitelistOverrideReasons) ? result.whitelistOverrideReasons : []);
  const hasWhitelistedMalicious = whitelistedMaliciousIndicators.length > 0;
  const whitelistSuppressed = !!result?.whitelistOverride;
  const whitelistMasksMalicious = hasWhitelistedMalicious && finalVerdictClass !== "pill-danger";
  const bannerVerdict = finalVerdict;
  const bannerVerdictClass = finalVerdictClass;
  const whitelistNoteHead = whitelistSuppressed
    ? `Verdict remains ${formatVerdict(finalVerdict)} — the malicious indicator(s) below were allowlisted by an administrator and excluded from scoring:`
    : "Allowlisted indicators flagged malicious by reputation, excluded from the verdict:";

  const verdictIcon =
    bannerVerdictClass === "pill-danger" ? <FaExclamationTriangle />
    : bannerVerdictClass === "pill-warn" ? <FaExclamationCircle />
    : bannerVerdictClass === "pill-ok" ? <FaCheckCircle />
    : <FaShieldAlt />;

  const handleExportPdf = async () => {
    setOptionsOpen(false);
    if (!result) return;

    try {
      const resp = await fetch("/api/phishing-analysis/export-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ report: result }),
      });

      if (!resp.ok) throw new Error("Failed to generate PDF");

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `phishing-report-${result.fileName || "email"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("[phishing] PDF export error:", e);
      setError("PDF export failed. Please try again.");
    }
  };

  const handleSendServiceNow = () => {
    setOptionsOpen(false);
    console.log("[phishing] Send to ServiceNow clicked");
  };

  const handleAiCorrelation = () => {
    // Keeping menu item for now if you want; remove if not needed.
    setOptionsOpen(false);
    console.log("[phishing] AI correlation clicked");
  };

  /* ============================================================
     RENDER
     ============================================================ */

  return (
    <div className="phishing-pages">
      <section className="phish-shell">
        {/* ------------------ Upload Section ------------------ */}
        <div className="card upload-card">
          <div className="upload-header">
            <div>
              <h2 className="section-title">Email Phishing Analysis</h2>
              <p className="section-desc">
                {mode === "single" ? (
                  <>
                    Upload a single <code>.eml</code> or <code>.msg</code> file exported from your
                    mail client. The platform will inspect headers, URLs, and attachments.
                  </>
                ) : (
                  <>
                    Upload up to {BULK_MAX_FILES} <code>.eml</code> / <code>.msg</code> files. Each
                    one is analysed sequentially; click any row to open its full report.
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="mode-switcher" role="tablist" aria-label="Analysis mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "single"}
              className={`mode-switch-btn ${mode === "single" ? "active" : ""}`}
              onClick={() => onSelectMode("single")}
            >
              <FaEnvelope />
              Single Email
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "bulk"}
              className={`mode-switch-btn ${mode === "bulk" ? "active" : ""}`}
              onClick={() => onSelectMode("bulk")}
            >
              <FaLayerGroup />
              Bulk Upload
            </button>
          </div>

          {mode === "single" && (
            <>
              <div className="controls-row">
                <label className="file-input-wrapper">
                  <span className="file-input-button">
                    <FaCloudUploadAlt />
                    Choose Email
                  </span>
                  <input type="file" accept=".eml,.msg" onChange={onChoose} className="file-input-native" />
                </label>

                <button onClick={onAnalyze} disabled={!file || loading || sensitiveChecking} className="analyze-btn">
                  {loading ? "Analyzing…" : sensitiveChecking ? "Checking…" : result ? "Reanalyze Email" : "Analyze Email"}
                </button>

                {loading && (
                  <button type="button" onClick={onCancelUpload} className="secondary-btn">
                    Cancel
                  </button>
                )}
              </div>

              {file && !loading && (
                <p className="file-info">
                  Selected file: <strong>{file.name}</strong> ({formatKB(file.size)} KB)
                  <button
                    type="button"
                    className="file-info-remove"
                    onClick={onRemoveFile}
                    title="Remove email"
                    aria-label={`Remove ${file.name}`}
                  >
                    <FaTimes />
                  </button>
                </p>
              )}

              {loading && (
                <div className="upload-progress">
                  <div className="progress-track">
                    <div className="progress-bar" style={{ width: `${progress}%` }} />
                  </div>
                  <small className="progress-label">{progress}%</small>
                </div>
              )}

              {error && <p className="error-text">{error}</p>}
            </>
          )}

          {mode === "bulk" && (
            <>
              <div
                className={`drop-zone ${bulkDragOver ? "drop-zone-active" : ""}`}
                onDragOver={onBulkDragOver}
                onDragLeave={onBulkDragLeave}
                onDrop={onBulkDrop}
              >
                <div className="controls-row">
                  <label className="file-input-wrapper">
                    <span className="file-input-button">
                      <FaLayerGroup />
                      Choose Emails
                    </span>
                    <input
                      type="file"
                      accept=".eml,.msg"
                      multiple
                      onChange={onBulkChoose}
                      className="file-input-native"
                    />
                  </label>

                  <button
                    onClick={onBulkAnalyze}
                    disabled={bulkFiles.length === 0 || bulkLoading || sensitiveChecking}
                    className="analyze-btn"
                  >
                    {bulkLoading
                      ? `Analyzing ${bulkCurrent} of ${bulkFiles.length}…`
                      : sensitiveChecking
                      ? "Checking…"
                      : `Analyze ${bulkFiles.length || ""} Email${
                          bulkFiles.length === 1 ? "" : "s"
                        }`.trim()}
                  </button>

                  {bulkLoading && (
                    <button type="button" onClick={onCancelBulk} className="secondary-btn">
                      Cancel
                    </button>
                  )}

                  {!bulkLoading && bulkFiles.length > 0 && (
                    <button type="button" onClick={onClearBulk} className="secondary-btn">
                      Clear
                    </button>
                  )}
                </div>

                {bulkFiles.length > 0 && (
                  <div className="bulk-chip-list">
                    {bulkFiles.map((f, i) => (
                      <div className="file-chip" key={`${f.name}-${f.size}-${i}`}>
                        <FaEnvelope className="file-chip-icon" />
                        <span className="file-chip-name">{f.name}</span>
                        <span className="file-chip-size">{formatKB(f.size)} KB</span>
                        {!bulkLoading && (
                          <button
                            type="button"
                            className="file-chip-remove"
                            onClick={() => onRemoveBulkFile(i)}
                            title="Remove file"
                            aria-label={`Remove ${f.name}`}
                          >
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {bulkFiles.length === 0 && !bulkLoading && (
                  <p className="drop-hint">
                    or drag &amp; drop multiple .eml / .msg files here (max {BULK_MAX_FILES})
                  </p>
                )}

                {bulkFiles.length > 0 && !bulkLoading && (
                  <p className="drop-hint">
                    {bulkFiles.length} file{bulkFiles.length === 1 ? "" : "s"} queued — pick or drop
                    more to add (max {BULK_MAX_FILES}).
                  </p>
                )}
              </div>

              {bulkLoading && (
                <div className="upload-progress">
                  <div className="progress-track">
                    <div
                      className="progress-bar"
                      style={{
                        width: `${
                          bulkFiles.length
                            ? Math.round((bulkCurrent / bulkFiles.length) * 100)
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                  <small className="progress-label">
                    Analyzing {bulkCurrent} of {bulkFiles.length}
                  </small>
                </div>
              )}

              {bulkError && <p className="error-text">{bulkError}</p>}
            </>
          )}
        </div>

        {/* ------------------ Bulk Results Table ------------------ */}
        {mode === "bulk" && selectedBulkIndex == null && bulkItems.length > 0 && (
          <div className="results-wrapper">
            <div className="card bulk-results-card">
              <div className="card-header bulk-results-header">
                <h3>Bulk Analysis Results</h3>
                <span className="bulk-summary-pill">
                  {bulkItems.filter((x) => x.status === "done").length} / {bulkItems.length}{" "}
                  analysed
                  {bulkItems.some((x) => x.status === "error") && (
                    <>
                      {" "}
                      ·{" "}
                      <span className="bulk-summary-errors">
                        {bulkItems.filter((x) => x.status === "error").length} error
                        {bulkItems.filter((x) => x.status === "error").length === 1 ? "" : "s"}
                      </span>
                    </>
                  )}
                </span>
              </div>

              <div className="table-wrapper">
                <table className="table bulk-table">
                  <thead>
                    <tr>
                      <th style={{ width: "32%" }}>Subject</th>
                      <th style={{ width: "26%" }}>Sender</th>
                      <th style={{ width: "26%" }}>Recipient</th>
                      <th style={{ width: "16%" }}>Verdict</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkItems.map((item, i) => {
                      const subj =
                        item.result?.headers?.subject ||
                        item.fileName ||
                        "(no subject)";
                      const sender = item.result?.headers?.from || "—";
                      const recipient = item.result?.headers?.to || "—";
                      const oneWord =
                        item.status === "error"
                          ? "Error"
                          : item.status === "analyzing"
                          ? "Analyzing"
                          : item.status === "pending"
                          ? "Pending"
                          : toOneWordVerdict(item.result?.verdict);
                      const pillCls =
                        item.status === "error"
                          ? "pill-danger"
                          : item.status === "analyzing" || item.status === "pending"
                          ? "pill-neutral"
                          : mapVerdictToClass(item.result?.verdict);
                      const clickable = item.status === "done" && item.result;
                      return (
                        <tr
                          key={i}
                          className={`bulk-row ${clickable ? "bulk-row-clickable" : ""}`}
                          onClick={() => {
                            if (clickable) {
                              setSelectedBulkIndex(i);
                              setActiveTab("header");
                              setShowRawHeader(false);
                              setOptionsOpen(false);
                            }
                          }}
                          title={
                            clickable
                              ? "Open detailed verdict"
                              : item.status === "error"
                              ? item.error || "Analysis failed"
                              : "Analysis still in progress"
                          }
                        >
                          <td className="bulk-subject-cell">
                            {item.status === "analyzing" && (
                              <FaSpinner className="bulk-spinner" />
                            )}
                            <span className="bulk-subject-text">{subj}</span>
                          </td>
                          <td className="mono mono-wrap">{sender}</td>
                          <td className="mono mono-wrap">{recipient}</td>
                          <td>
                            <span className={`pill ${pillCls}`}>{oneWord}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ------------------ Results ------------------ */}
        {result && (
          <div className="results-wrapper">
            {mode === "bulk" && selectedBulkIndex != null && (
              <button
                type="button"
                className="bulk-back-btn"
                onClick={() => {
                  setSelectedBulkIndex(null);
                  setOptionsOpen(false);
                  setShowRawHeader(false);
                }}
              >
                <FaArrowLeft /> Back to results
              </button>
            )}

            {/* ------------------ Overall Analysis Outcome ------------------ */}
            <div className="card overall-outcome-card">
              <div className="card-header overall-header">
                <h3>Overall Analysis Outcome</h3>

                <div className="card-options">
                  <button
                    type="button"
                    className="clear-verdict-btn"
                    onClick={() => {
                      if (mode === "bulk") {
                        setSelectedBulkIndex(null);
                        setOptionsOpen(false);
                        setShowRawHeader(false);
                      } else {
                        clearStandardResult();
                        setFile(null);
                        setError("");
                        setProgress(0);
                      }
                    }}
                    title={mode === "bulk" ? "Back to results" : "Clear verdict"}
                    aria-label={mode === "bulk" ? "Back to results" : "Clear verdict"}
                  >
                    <FaTimes />
                  </button>

                  <button
                    type="button"
                    className="card-options-btn"
                    onClick={() => setOptionsOpen((open) => !open)}
                  >
                    <span className="sr-only">More options</span>
                    <FaEllipsisV />
                  </button>

                  {optionsOpen && (
                    <div className="card-options-menu">
                      <button type="button" className="card-options-item" onClick={handleExportPdf}>
                        Export as PDF
                      </button>
                      <button type="button" className="card-options-item" onClick={handleSendServiceNow}>
                        Send to ServiceNow
                      </button>
                      <button type="button" className="card-options-item" onClick={handleAiCorrelation}>
                        AI correlation
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* ── VERDICT BANNER ── */}
              <div
                className={`verdict-banner vb-${bannerVerdictClass.replace("pill-", "")} ${
                  hasWhitelistedMalicious ? "vb-whitelisted" : ""
                } ${whitelistMasksMalicious ? "vb-whitelist-danger" : ""}`}
              >
                <div className="vb-glow" />
                <div className="vb-icon">{verdictIcon}</div>
                <div className="vb-content">
                  <div className="vb-label">
                    Verdict
                    {hasWhitelistedMalicious && (
                      <span
                        className={`vb-override-tag ${
                          whitelistMasksMalicious ? "vb-override-tag-danger" : ""
                        }`}
                      >
                        {whitelistSuppressed
                          ? `ALLOWLISTED IoC EXCLUDED — would otherwise be ${formatVerdict(result.originalVerdict)}`
                          : "ALLOWLISTED IoC EXCLUDED FROM VERDICT"}
                      </span>
                    )}
                  </div>
                  <div className="vb-value">{formatVerdict(bannerVerdict)}</div>
                  {raisedBySandbox && (
                    <div className="vb-override-tag">
                      {detoMalicious
                        ? "ESCALATED — a sandbox detonation confirmed malicious behavior"
                        : `ESCALATED — a sandbox detonation flagged suspicious behavior; routed to ${formatVerdict(finalVerdict)}`}
                    </div>
                  )}
                  {/* Sandbox detonation as a third engine */}
                  {detoComplete && detoVerdict &&
                    !["pending", "unknown"].includes(String(detoVerdict).toLowerCase()) && (
                      <div className="vb-engines">
                        <div className="vb-engine-line">
                          <span className="vb-engine-tag">Sandbox</span>
                          <span className={`pill ${mapVerdictToClass(detoVerdict)}`}>
                            {formatVerdict(detoVerdict)}
                          </span>
                          <span className="vb-engine-reason">
                            {detoMalicious
                              ? "Detonation confirmed malicious behavior."
                              : detoSuspicious
                              ? "Detonation observed suspicious behavior."
                              : "Detonation observed no malicious behavior."}
                          </span>
                        </div>
                      </div>
                    )}
                  {raisedBySandbox && (
                    <div className="vb-determination">
                      Advanced sandbox detonation returned “{formatVerdict(detoVerdict)}” — overall verdict raised to {formatVerdict(finalVerdict)}.
                    </div>
                  )}
                  {analystReviewAdvised && (
                    <div className="vb-analyst-review" role="note">
                      <strong>⚑ Analyst review advised</strong>
                      {analystReviewReason ? ` — ${analystReviewReason}` : ""}
                    </div>
                  )}
                  <div className="vb-sub">
                    {result.fileName} · {senderType} sender · {fromDomainText}
                  </div>
                  {hasWhitelistedMalicious && (
                    <div className="vb-whitelist-note">
                      <div className="vb-determination">{whitelistNoteHead}</div>
                      <ul className="vb-override-reasons">
                        {whitelistedMaliciousIndicators.map((r, i) => (
                          <li key={i}>{r.note}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <HomoglyphFindings homoglyphs={result.homoglyphs} />
              <CustomRuleFindings customRules={result.customRules} />

              {/* ── SIGNAL SCORECARD ── */}
              <div className="signal-row">
                {[
                  { label: "Header Risk",  value: headerSec?.riskLevel?.toUpperCase() || "?", cls: headerRiskClass },
                  { label: "SPF",          value: spfResult.toUpperCase(),   cls: spfResult   === "pass" ? "pill-ok" : "pill-danger" },
                  { label: "DKIM",         value: dkimResult.toUpperCase(),  cls: dkimResult  === "pass" ? "pill-ok" : "pill-danger" },
                  { label: "DMARC",        value: dmarcResult.toUpperCase(), cls: dmarcResult === "pass" ? "pill-ok" : "pill-danger" },
                  { label: "Attachments",  value: String(attachmentCount),   cls: attachmentCount > 0   ? "pill-warn" : "pill-ok" },
                  { label: "URLs",         value: String(urlsArr.length),    cls: urlsArr.length  > 0   ? "pill-warn" : "pill-ok" },
                  { label: "QR Codes",     value: String(qrCount),           cls: qrCount         > 0   ? "pill-warn" : "pill-ok" },
                ].map(({ label, value, cls }) => (
                  <div key={label} className={`signal-chip sc-${cls.replace("pill-", "")}`}>
                    <div className="sc-value">{value}</div>
                    <div className="sc-label">{label}</div>
                  </div>
                ))}
              </div>

              {adv.showAdvanced && (
                <div style={{ display: "flex", justifyContent: "flex-end", margin: "14px 0" }}>
                  <AdvancedAnalysisButton adv={adv} onStart={() => setActiveTab("advanced")} />
                </div>
              )}

              <div className="overall-summary-block">
                <h4>Analysis Summary</h4>

                <div className="analysis-summary-text">
                  <p className="analysis-summary-intro">
                    This email was received from an <strong>{senderType}</strong> sender{" "}
                    <strong>{fromAddress}</strong> using the domain{" "}
                    <strong className="summary-entity">
                      {fromDomainText}
                      {vtDomainScore && (
                        <span className={vtDomainScoreClass} title="VirusTotal detections / total engines">
                          VirusTotal : {vtDomainScore}
                        </span>
                      )}
                      {fromDomainText && fromDomainText !== "unknown" && (
                        <IocInfoPopover type="domain" value={fromDomainText} />
                      )}
                      {vtDomainUrl && (
                        <a
                          href={vtDomainUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="summary-entity-link"
                          title="Open domain in VirusTotal"
                        >
                          <FaExternalLinkAlt />
                        </a>
                      )}
                    </strong>
                    {sourceIp && (
                      <>
                        {" "}
                        and originating from IP{" "}
                        <strong className="summary-entity">
                          {sourceIp}
                          <IocInfoPopover type="ip" value={sourceIp} />
                          {vtIpUrl && (
                            <a
                              href={vtIpUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="summary-entity-link"
                              title="Open IP in VirusTotal"
                            >
                              <FaExternalLinkAlt />
                            </a>
                          )}
                        </strong>
                      </>
                    )}
                    . The evaluated header risk is <strong>{headerSec?.riskLevel || "unknown"}</strong>.
                  </p>

                  <ul className="analysis-summary-list">
                    <li>
                      <strong>Sender controls</strong> – {spfSummary}; {dkimSummary}; {dmarcSummary}.
                    </li>

                    <li>
                      <strong>Attachments</strong> –{" "}
                      {attachmentCount === 0 && "No attachments present."}
                      {attachmentCount === 1 && primaryAttachment && (
                        <>
                          1 attachment (<span>{primaryAttachment.name}</span>, <span>{primaryAttachment.mime}</span>,{" "}
                          <span>{primaryAttachment.sizeKB} KB</span>) with SHA1{" "}
                          <span className="mono">{primaryAttachment.sha1}</span> and SHA256{" "}
                          <span className="mono">{primaryAttachment.sha256}</span>.
                        </>
                      )}
                      {attachmentCount > 1 &&
                        `${attachmentCount} attachments present; all hashed with SHA1 and SHA256.`}
                    </li>

                    <li>
                      <strong>OSINT validations</strong> –{" "}
                      {urlsArr.length === 0 ? (
                        "no clickable URLs extracted."
                      ) : (
                        <>
                          {urlsArr.length} URL{urlsArr.length > 1 ? "s" : ""} analysed. For the primary URL: VT{" "}
                          {vtLabel}, AbuseIPDB {abuseLabel}, IPQS {ipqsLabel}, URLScan.io {urlscanLabel}, AlienVault OTX{" "}
                          {otxLabel}.
                        </>
                      )}
                    </li>

                    <li>
                      <strong>Social Engineering</strong> –{" "}
                      {!socialIntent
                        ? "not available."
                        : `level ${String(socialLevel).toUpperCase()} (score ${socialScore}); ${socialSummaryText}`}
                    </li>

                    <li>
                      <strong>QR intelligence</strong> –{" "}
                      {qrCount === 0
                        ? "no QR codes detected in body or attachments."
                        : `${qrCount} QR code${qrCount > 1 ? "s" : ""} decoded (example: ${qrPreview}).`}
                    </li>

                    <li>
                      <strong>Homoglyph pattern</strong> –{" "}
                      {!result?.homoglyphs ? (
                        <em>not evaluated for this analysis (re-run after adding protected domains).</em>
                      ) : result.homoglyphs.matchedCount > 0 ? (
                        <>
                          <strong className="summary-flag-yes">Yes</strong> —{" "}
                          {result.homoglyphs.findings[0].domain} resembles protected domain{" "}
                          {result.homoglyphs.findings[0].matched}
                          {result.homoglyphs.matchedCount > 1
                            ? ` (and ${result.homoglyphs.matchedCount - 1} more)`
                            : ""}
                          .
                        </>
                      ) : (
                        <>
                          <strong className="summary-flag-no">No</strong> — no sender, reply-to, or link
                          domain resembles a protected domain.
                        </>
                      )}
                    </li>

                    <li>
                      <strong>Final verdict</strong> – classified as <strong>{formatVerdict(finalVerdict)}</strong> based on the
                      combination of header risk, sender authentication results, attachment profile and OSINT outcomes.
                    </li>
                  </ul>
                </div>
              </div>

              <div className="overall-summary-block">
                <h4>Spoofing & Header Anomalies</h4>

                {headerSec.anomalies?.length > 0 ? (
                  <ul className="mono-list">
                    {headerSec.anomalies.map((a, i) => (
                      <li key={i}>
                        <span
                          className={`pill ${
                            a.severity === "high" ? "pill-danger" : a.severity === "medium" ? "pill-warn" : "pill-info"
                          }`}
                        >
                          {a.type} ({a.severity})
                        </span>
                        <span className="anomaly-text"> – {a.description}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="placeholder">No header anomalies detected.</p>
                )}
              </div>
            </div>

            {/* ------------------ Tabs ------------------ */}
            <div className="phish-tabs">
              <button
                type="button"
                className={`phish-tab-btn ${activeTab === "header" ? "active" : ""}`}
                onClick={() => setActiveTab("header")}
              >
                Header Analysis
              </button>

              <button
                type="button"
                className={`phish-tab-btn ${activeTab === "controls" ? "active" : ""}`}
                onClick={() => setActiveTab("controls")}
              >
                Sender Controls
              </button>

              <button
                type="button"
                className={`phish-tab-btn ${activeTab === "attachments" ? "active" : ""}`}
                onClick={() => setActiveTab("attachments")}
              >
                Attachments
              </button>

              <button
                type="button"
                className={`phish-tab-btn ${activeTab === "body" ? "active" : ""}`}
                onClick={() => setActiveTab("body")}
              >
                Email Body
              </button>

              <button
                type="button"
                className={`phish-tab-btn ${activeTab === "analysis" ? "active" : ""}`}
                onClick={() => setActiveTab("analysis")}
              >
                Social Engineering
              </button>

              <button
                type="button"
                className={`phish-tab-btn ${activeTab === "qr" ? "active" : ""}`}
                onClick={() => setActiveTab("qr")}
              >
                QR Intelligence
              </button>

              <button
                type="button"
                className={`phish-tab-btn ${activeTab === "urls" ? "active" : ""}`}
                onClick={() => setActiveTab("urls")}
              >
                URLs & Threat Intel
              </button>

              {adv.showAdvanced && (
                <button
                  type="button"
                  className={`phish-tab-btn ${activeTab === "advanced" ? "active" : ""}`}
                  onClick={() => setActiveTab("advanced")}
                >
                  Advanced Analysis
                </button>
              )}
            </div>

            {/* ------------------ Tab Content ------------------ */}

            {activeTab === "header" && (
              <div className="results-grid">
                <div className="results-column">
                  <div className="card">
                    <div className="card-header">
                      <h3>Header Analysis</h3>
                    </div>

                    <div className="kv">
                      <div>
                        <span>From</span>
                        <code>{result.headers?.from}</code>
                      </div>
                      <div>
                        <span>To</span>
                        <code>{result.headers?.to}</code>
                      </div>
                      <div>
                        <span>Subject</span>
                        <code>{result.headers?.subject}</code>
                      </div>
                      <div>
                        <span>Reply-To</span>
                        <code>{result.headers?.replyTo || "—"}</code>
                      </div>
                      <div>
                        <span>Return-Path</span>
                        <code>{result.headers?.returnPath || "—"}</code>
                      </div>

                      {replyCheck && (
                        <div className="replyto-status">
                          <span className="replyto-label">From vs Reply-To</span>
                          <span className={`pill ${replyRiskClass}`}>
                            {replyCheck.status === "match"
                              ? "Addresses match"
                              : replyCheck.status === "domain-match-local-mismatch"
                              ? "Caution: mailbox differs"
                              : replyCheck.status === "domain-mismatch"
                              ? "Suspicious: domain mismatch"
                              : "No Reply-To header"}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="subpanel">
                      <div className="subpanel-title">
                        <span>Received Path</span>
                        <span className="subpanel-hint">*Origin at the top, delivery at the bottom</span>
                      </div>

                      {receivedFlow.length > 0 ? (
                        <div className="received-flow">
                          <div
                            className={`hop-envelope hop-envelope-${receivedFlow[0]?.risk || "ok"}`}
                            aria-hidden="true"
                          >
                            <FaEnvelope />
                          </div>
                          {receivedFlow.map((hop, i) => (
                            <React.Fragment key={i}>
                              <div className={`hop hop-risk-${hop.risk}`}>
                                <div className="hop-header">
                                  <span className="hop-role">{hop.role}</span>
                                  <span
                                    className={`pill hop-pill ${hopPillClass(hop.risk)}`}
                                  >
                                    {hop.riskLabel}
                                  </span>
                                </div>
                                <pre className="hop-line mono">{hop.line}</pre>
                              </div>
                              {i < receivedFlow.length - 1 && (
                                <div className="hop-arrow">
                                  <FaArrowDown />
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      ) : (
                        <p className="placeholder">No Received headers found.</p>
                      )}
                    </div>

                    <div className="subpanel raw-header-subpanel">
                      <div className="subpanel-title">
                        <span>Raw Headers</span>
                        <div className="raw-header-actions">
                          {copyStatus && (
                            <span
                              className={`copy-status ${copyStatus === "copied" ? "copy-status-ok" : "copy-status-bad"}`}
                            >
                              {copyStatus === "copied" ? "Copied" : "Copy failed"}
                            </span>
                          )}
                          <button type="button" className="link-btn" onClick={handleCopyHeaders}>
                            <FaCopy />
                          </button>
                          <button type="button" className="link-btn" onClick={() => setShowRawHeader((v) => !v)}>
                            {showRawHeader ? "Hide" : "Show"}
                          </button>
                        </div>
                      </div>

                      {showRawHeader && (
                        <pre className="raw-headers mono">{result.headers?.raw || "Raw header not available."}</pre>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "controls" && (
              <div className="results-grid">
                <div className="results-column">
                  <div className="card">
                    <div className="card-header">
                      <h3>Sender Controls</h3>
                    </div>

                    <div className="checks">
                      <div className="check-row">
                        <span className="check-label">SPF</span>
                        <span className={`pill ${spfResult === "pass" ? "pill-ok" : "pill-danger"}`}>
                          {spfResult.toUpperCase()}
                        </span>
                      </div>
                      <p className="check-detail">{spfDetail || "No SPF info available."}</p>

                      <div className="check-row">
                        <span className="check-label">DKIM</span>
                        <span className={`pill ${dkimResult === "pass" ? "pill-ok" : "pill-danger"}`}>
                          {dkimResult.toUpperCase()}
                        </span>
                      </div>
                      <p className="check-detail">{dkimDetail || "No DKIM info available."}</p>

                      <div className="check-row">
                        <span className="check-label">DMARC</span>
                        <span className={`pill ${dmarcResult === "pass" ? "pill-ok" : "pill-danger"}`}>
                          {dmarcResult.toUpperCase()}
                        </span>
                      </div>
                      <p className="check-detail">{dmarcDetail || "No DMARC info available."}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "attachments" && (
              <div className="results-grid">
                <div className="results-column">
                  <div className="card">
                    <div className="card-header">
                      <h3>Attachments</h3>
                    </div>

                    <div className="table-wrapper">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>File</th>
                            <th>Type</th>
                            <th>Size (KB)</th>
                            <th>SHA1</th>
                            <th>SHA256</th>
                            <th>VirusTotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(result.attachments || []).length === 0 && (
                            <tr>
                              <td colSpan="6" className="placeholder-row">
                                No attachments detected.
                              </td>
                            </tr>
                          )}

                          {(result.attachments || []).map((a, i) => {
                            const vt = a.vt || null;
                            const vtVerdict = vt?.verdict || "unknown";

                            let vtCellLabel = "N/A";
                            if (vt) {
                              if (vtVerdict === "malicious" || vtVerdict === "suspicious") {
                                const pos = vt.positives ?? "?";
                                const tot = vt.total ?? "?";
                                vtCellLabel = `${formatVerdict(vtVerdict)} (${pos}/${tot})`;
                              } else if (vtVerdict === "clean") {
                                vtCellLabel = "clean";
                              } else if (vtVerdict === "not-configured") {
                                vtCellLabel = "not configured";
                              } else {
                                vtCellLabel = formatVerdict(vtVerdict);
                              }
                            }

                            return (
                              <tr key={i}>
                                <td>{a.name}</td>
                                <td className="mono">{a.mime}</td>
                                <td>{a.sizeKB}</td>
                                <td className="mono mono-wrap hash">{a.sha1}</td>
                                <td className="mono mono-wrap hash">{a.sha256}</td>
                                <td>
                                  {vt ? (
                                    vt.rateLimited ? (
                                      <RateTag />
                                    ) : (
                                    <a
                                      href={vt.reportLink}
                                      target="_blank"
                                      rel="noreferrer"
                                      className={`vt-pill vt-pill-${vtVerdict}`}
                                      title="View VirusTotal report"
                                    >
                                      {vtCellLabel}
                                    </a>
                                    )
                                  ) : (
                                    <span className="muted">N/A</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "body" && (
              <div className="results-grid">
                <div className="results-column">
                  <div className="card email-body-card">
                    <div className="card-header">
                      <h3>Email Body</h3>
                    </div>

                    {result.emailBodyText ? (
                      <div className="email-body-wrapper">
                        <pre className="email-body-text">{result.emailBodyText}</pre>
                      </div>
                    ) : (
                      <p className="placeholder">Email body not available or empty.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "analysis" && (
              <div className="results-grid">
                <div className="results-column">
                  <div className="card">
                    <div className="card-header">
                      <h3>Email Body Analysis</h3>
                    </div>

                    {!socialIntent ? (
                      <p className="placeholder">No social engineering analysis available.</p>
                    ) : (
                      <>
                        <div className="social-header">
                          <div className="social-level-wrap">
                            <div className="social-level-label">Risk Level</div>
                            <span className={`pill social-level-badge ${socialPillClass}`}>
                              {String(socialLevel).toUpperCase()}
                            </span>
                          </div>
                          <div className="social-score-wrap">
                            <div className="social-score-label">Indicator Score</div>
                            <div className="social-score-meter">
                              <div className="social-score-track">
                                <div
                                  className={`social-score-fill ${socialPillClass}`}
                                  style={{ width: `${Math.min(100, socialScore * 10)}%` }}
                                />
                              </div>
                              <span className="social-score-num">{socialScore}</span>
                            </div>
                          </div>
                        </div>

                        <p className="ai-text social-summary">{socialSummaryText}</p>

                        {(() => {
                          const entries = Object.entries(socialIndicators || {})
                            .map(([k, hits]) => ({ key: k, hits: Array.isArray(hits) ? hits : [] }))
                            .filter((e) => e.hits.length > 0);

                          if (entries.length === 0) {
                            return (
                              <p className="placeholder" style={{ marginTop: 10 }}>
                                No matched social engineering keywords were found.
                              </p>
                            );
                          }

                          return (
                            <div className="social-categories">
                              <p className="social-cat-hint">
                                *Keyword-based matches — review in context
                              </p>
                              {entries.map((e) => (
                                <div key={e.key} className="social-cat-card">
                                  <div className="social-cat-name">{humanizeCat(e.key)}</div>
                                  <div className="social-cat-hits">
                                    {e.hits.map((hit, i) => (
                                      <span key={i} className="social-hit-tag">{hit}</span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "qr" && (
              <div className="results-grid">
                <div className="results-column">
                  <div className="card">
                    <div className="card-header">
                      <h3>QR Intelligence</h3>
                    </div>

                    {(() => {
                      const all = allQrForSummary;

                      if (all.length === 0) {
                        return <p className="placeholder">No QR codes detected in body or attachments.</p>;
                      }

                      return (
                        <div className="table-wrapper">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Source</th>
                                <th>Image</th>
                                <th>Type</th>
                                <th>Decoded</th>
                              </tr>
                            </thead>
                            <tbody>
                              {all.map((x, i) => {
                                const parsed = parseQrPayload(x.qrText);
                                return (
                                  <tr key={i}>
                                    <td>{x.source}</td>
                                    <td className="mono mono-wrap">{x.name}</td>
                                    <td>
                                      <span className="pill pill-info">{parsed.type}</span>
                                    </td>
                                    <td>{renderQrValue(parsed)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "urls" && (
              <div className="results-grid">
                <div className="results-column">
                  <div className="card urls-card-wide">
                    <div className="card-header urls-card-header">
                      <h3>URLs & Threat Intel</h3>
                      {result.urls?.length > 0 && (
                        <span className="urls-count-pill">
                          {result.urls.length} URL{result.urls.length > 1 ? "s" : ""} analysed
                        </span>
                      )}
                    </div>

                    <div className="table-wrapper urls-table-wrapper">
                      <table className="table url-intel-table">
                        <thead>
                          <tr>
                            <th>URL</th>
                            <th>Overall</th>
                            <th>VirusTotal</th>
                            <th>AbuseIPDB</th>
                            <th>IPQualityScore</th>
                            <th>URLScan.io</th>
                            <th>AlienVault OTX</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(result.urls || []).length === 0 && (
                            <tr>
                              <td colSpan="7" className="placeholder-row">
                                No URLs extracted.
                              </td>
                            </tr>
                          )}

                          {(result.urls || []).map((u, i) => {
                            const overallVerdict = u.overallVerdict || u.vt?.verdict || "unknown";

                            const abuseScore = u.abuseIpdb?.score;
                            const abuseLabel2 =
                              u.abuseIpdb?.classification ||
                              (typeof abuseScore === "number" ? `${abuseScore}/100` : "unknown");

                            const ipqsLabel2 =
                              typeof u.ipqs?.score === "number" ? `${u.ipqs.score}/100` : u.ipqs?.risk || "unknown";

                            const urlscanVerdict = u.urlscan?.verdict || "unknown";
                            const otxVerdict = u.otx?.verdict || "unknown";

                            return (
                              <tr key={i} className={u.whitelisted ? "url-row-whitelisted" : ""}>
                                <td className="mono mono-wrap url-cell">
                                  <span className="url-main">{u.url}</span>
                                  {u.whitelisted && (
                                    <span
                                      className="wl-badge"
                                      title={u.whitelistNote || "Allowlisted by an administrator"}
                                    >
                                      <FaShieldAlt /> Whitelisted
                                    </span>
                                  )}
                                </td>

                                <td>
                                  <span className={`pill ${mapVerdictToClass(overallVerdict)}`}>
                                    {u.whitelisted ? "Whitelisted" : mapVerdictToLabel(overallVerdict)}
                                  </span>
                                </td>

                                <td>
                                  {u.vt ? (
                                    u.vt.rateLimited ? (
                                      <RateTag />
                                    ) : (
                                    <div className="intel-cell">
                                      <span className={`pill ${mapVerdictToClass(u.vt.verdict)}`}>
                                        {mapVerdictToLabel(u.vt.verdict)}
                                      </span>
                                      <div className="intel-meta">
                                        {typeof u.vt.positives === "number" && typeof u.vt.total === "number"
                                          ? `${u.vt.positives}/${u.vt.total} detections`
                                          : "detections not available"}
                                      </div>
                                      {u.vt.last_scan && <div className="intel-meta">Last scan: {u.vt.last_scan}</div>}
                                      {u.vt.reportLink && (
                                        <div className="intel-meta">
                                          <a href={u.vt.reportLink} target="_blank" rel="noreferrer" className="intel-link">
                                            View report
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                    )
                                  ) : (
                                    <span className="intel-na">N/A</span>
                                  )}
                                </td>

                                <td>
                                  {u.abuseIpdb ? (
                                    u.abuseIpdb.rateLimited ? (
                                      <RateTag />
                                    ) : (
                                    <div className="intel-cell">
                                      <span className={`pill ${mapVerdictToClass(u.abuseIpdb.classification || abuseScore)}`}>
                                        {abuseLabel2}
                                      </span>

                                      {typeof abuseScore === "number" && <div className="intel-meta">Score: {abuseScore}/100</div>}
                                      {u.abuseIpdb.totalReports !== undefined && (
                                        <div className="intel-meta">Reports: {u.abuseIpdb.totalReports}</div>
                                      )}
                                      {u.abuseIpdb.lastReportedAt && (
                                        <div className="intel-meta">Last report: {u.abuseIpdb.lastReportedAt}</div>
                                      )}
                                      {u.abuseIpdb.reportLink && (
                                        <div className="intel-meta">
                                          <a
                                            href={u.abuseIpdb.reportLink}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="intel-link"
                                          >
                                            View report
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                    )
                                  ) : (
                                    <span className="intel-na">N/A</span>
                                  )}
                                </td>

                                <td>
                                  {u.ipqs ? (
                                    u.ipqs.rateLimited ? (
                                      <RateTag />
                                    ) : (
                                    <div className="intel-cell">
                                      <span className={`pill ${mapVerdictToClass(u.ipqs.risk || u.ipqs.score)}`}>
                                        {mapVerdictToLabel(u.ipqs.risk || "unknown")}
                                      </span>

                                      {typeof u.ipqs.score === "number" && <div className="intel-meta">Score: {ipqsLabel2}</div>}

                                      <div className="intel-meta">
                                        {u.ipqs.isProxy && "Proxy "}
                                        {u.ipqs.isVpn && "VPN "}
                                        {u.ipqs.isTor && "Tor "}
                                        {!u.ipqs.isProxy && !u.ipqs.isVpn && !u.ipqs.isTor && "No proxy/VPN flags"}
                                      </div>
                                    </div>
                                    )
                                  ) : (
                                    <span className="intel-na">N/A</span>
                                  )}
                                </td>

                                <td>
                                  {u.urlscan ? (
                                    u.urlscan.rateLimited ? (
                                      <RateTag />
                                    ) : (
                                    <div className="intel-cell">
                                      <span className={`pill ${mapVerdictToClass(urlscanVerdict)}`}>
                                        {mapVerdictToLabel(urlscanVerdict)}
                                      </span>
                                      {u.urlscan.scanDate && <div className="intel-meta">Last scan: {u.urlscan.scanDate}</div>}
                                      {u.urlscan.reportLink && (
                                        <div className="intel-meta">
                                          <a
                                            href={u.urlscan.reportLink}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="intel-link"
                                          >
                                            View report
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                    )
                                  ) : (
                                    <span className="intel-na">N/A</span>
                                  )}
                                </td>

                                <td>
                                  {u.otx ? (
                                    u.otx.rateLimited ? (
                                      <RateTag />
                                    ) : (
                                    <div className="intel-cell">
                                      <span className={`pill ${mapVerdictToClass(otxVerdict)}`}>
                                        {mapVerdictToLabel(otxVerdict)}
                                      </span>
                                      {u.otx.pulseCount !== undefined && <div className="intel-meta">Pulses: {u.otx.pulseCount}</div>}
                                      {u.otx.reportLink && (
                                        <div className="intel-meta">
                                          <a href={u.otx.reportLink} target="_blank" rel="noreferrer" className="intel-link">
                                            View report
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                    )
                                  ) : (
                                    <span className="intel-na">N/A</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "advanced" && <AdvancedAnalysisCard adv={adv} />}
          </div>
        )}
      </section>

      {confirmSensitive && (
        <div
          className="pa-confirm-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) confirmSensitive.onCancel(); }}
        >
          <div className="pa-confirm-modal">
            <div className="pa-confirm-header">
              <FaExclamationTriangle className="pa-confirm-icon" />
              <span>Confidential Email{confirmSensitive.isBulk ? "s" : ""} Detected</span>
            </div>
            <div className="pa-confirm-body">
              {confirmSensitive.isBulk ? (
                <>
                  <p>
                    <strong>{confirmSensitive.sensitiveFiles.length}</strong> of the selected emails
                    appear to be marked as confidential or restricted:
                  </p>
                  <ul className="pa-confirm-reasons">
                    {confirmSensitive.sensitiveFiles.map((f, i) => (
                      <li key={i}>
                        <span className="pa-confirm-reason-label">{f.fileName}</span>
                        <span className="pa-confirm-reason-value">{f.classification || "Confidential"}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <p>
                    This email appears to be marked as{" "}
                    <strong>{confirmSensitive.classification || "confidential"}</strong>.
                  </p>
                  <ul className="pa-confirm-reasons">
                    {(confirmSensitive.reasons || []).map((r, i) => (
                      <li key={i}>
                        <span className="pa-confirm-reason-label">{r.label}:</span>
                        <span className="pa-confirm-reason-value">{r.value}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <p className="pa-confirm-warning">
                Processing sensitive emails may be subject to your organization's data handling
                policies. Ensure you are authorized to submit this email for analysis.
              </p>
            </div>
            <div className="pa-confirm-actions">
              <button className="pa-confirm-cancel" onClick={confirmSensitive.onCancel}>
                Cancel
              </button>
              <button className="pa-confirm-proceed" onClick={confirmSensitive.onConfirm}>
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Choose-platform modal (only when 2+ sandboxes & no default) ── */}
      <AdvancedAnalysisModal adv={adv} />
    </div>
  );
}